import { ReactNode } from "react";
import { clsx } from "clsx";
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
} from "@heroicons/react/16/solid";
import { Badge } from "@/catalyst/badge.tsx";

/**
 * The fields of a dialog or a side panel: stacked `FormRow`s, one distance
 * apart. About half the distance between a card's rows, which also have a rule
 * between them; a dialog has neither the room nor a rule per row. One that is
 * long enough to need structure puts its rows in `FormGroup`s.
 */
export function FormStack({
    className,
    children,
}: {
    className?: string;
    children: ReactNode;
}) {
    return (
        <div className={clsx("flex flex-col gap-7", className)}>{children}</div>
    );
}

/**
 * Rows of a `FormStack` that belong together, set apart from the group before
 * them by the card's dashed rule. A rule per group says which fields go
 * together; a rule per row would only say there are rows.
 *
 * For a form of some length. A dialog of a few rows is just rows.
 */
export function FormGroup({
    title,
    children,
}: {
    /** Names the group, when its rows do not already say what they share. */
    title?: string;
    children: ReactNode;
}) {
    return (
        <div
            role={"group"}
            aria-label={title}
            className={
                "border-t border-dashed border-zinc-950/5 pt-7 first:border-t-0 first:pt-0 dark:border-white/5"
            }
        >
            {title && (
                <div
                    className={
                        "mb-3 text-base/6 font-semibold text-default sm:text-sm/6"
                    }
                >
                    {title}
                </div>
            )}
            <div className={"flex flex-col gap-7"}>{children}</div>
        </div>
    );
}

/** What a section says while it has no rows yet. */
export function FormEmpty({ children }: { children: ReactNode }) {
    return (
        <p className={"text-pretty py-6 text-sm text-subtle last:pb-0"}>
            {children}
        </p>
    );
}

/**
 * What is wrong with a field's value, under its control. One look for every
 * form, so an error reads the same in a card and in a dialog.
 */
export function FormError({ children }: { children?: ReactNode }) {
    if (!children) return null;
    return (
        <p role={"alert"} className={"text-sm text-red-600 dark:text-red-400"}>
            {children}
        </p>
    );
}

/**
 * How much of a field's limit is used, under the field on the right. Amber for
 * the last tenth, so the limit is not a surprise.
 */
export function FormCount({ used, max }: { used: number; max: number }) {
    return (
        <p
            // Read out on request, not on every keystroke.
            aria-live={"off"}
            className={clsx(
                "-mt-1 text-right text-xs tabular-nums",
                used >= max * 0.9
                    ? "text-amber-700 dark:text-amber-400"
                    : "text-subtle",
            )}
        >
            {used.toLocaleString()} / {max.toLocaleString()}
        </p>
    );
}

/**
 * A control shorter than a field (a switch, a set of radios, a button), given a
 * field's height so its label lines up as it does beside an input, and kept at
 * its own width instead of stretched to the measure.
 */
export function FormInline({ children }: { children: ReactNode }) {
    return (
        <div className={"flex min-h-9 flex-wrap items-center gap-x-4 gap-y-2"}>
            {children}
        </div>
    );
}

/**
 * Whether the last change landed, for a form that saves as you go. Nothing
 * until the first change.
 */
export function SaveStatus({
    isSaving,
    isSaved,
    isError,
}: {
    isSaving: boolean;
    isSaved: boolean;
    isError: boolean;
}) {
    return (
        // A flex box, not a line of text: a badge sitting on a text line makes
        // the line taller than the badge, and the card's header would grow by
        // that much the moment the first change is saved.
        <span role={"status"} className={"flex whitespace-nowrap"}>
            {isSaving && <Badge color={"zinc"}>Saving…</Badge>}
            {!isSaving && isError && (
                <Badge color={"red"}>
                    <ExclamationCircleIcon className={"size-4"} />
                    Not saved
                </Badge>
            )}
            {!isSaving && isSaved && (
                <Badge color={"indigo"}>
                    <CheckCircleIcon className={"size-4"} />
                    Changes saved
                </Badge>
            )}
        </span>
    );
}
